/**
 * Small auth helpers shared between web AuthDialog/SignInScreen and any
 * mobile screens that prefer to call typed wrappers rather than the
 * Firebase SDK directly. All functions throw on failure — callers should
 * surface the message via toast / inline error.
 */
import {
  Auth,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  UserCredential,
} from 'firebase/auth';

export type AuthMode = 'signin' | 'signup';

export const signInWithGoogle = (
  auth: Auth,
  provider: GoogleAuthProvider
): Promise<UserCredential> => signInWithPopup(auth, provider);

export const signInWithEmail = (
  auth: Auth,
  email: string,
  password: string
): Promise<UserCredential> => signInWithEmailAndPassword(auth, email, password);

export const signUpWithEmail = (
  auth: Auth,
  email: string,
  password: string
): Promise<UserCredential> =>
  createUserWithEmailAndPassword(auth, email, password);

export const sendResetEmail = (auth: Auth, email: string): Promise<void> =>
  sendPasswordResetEmail(auth, email);

export const signOutUser = (auth: Auth): Promise<void> => signOut(auth);

/**
 * Convert a Firebase auth error to a friendly message.
 * Firebase error codes are documented at:
 * https://firebase.google.com/docs/auth/admin/errors
 */
export const friendlyAuthError = (err: unknown): string => {
  const code =
    typeof err === 'object' && err !== null && 'code' in err
      ? String((err as { code?: unknown }).code ?? '')
      : '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address looks malformed.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Email or password is incorrect.';
    case 'auth/email-already-in-use':
      return 'An account already exists with that email. Try signing in.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in window was closed before completing.';
    case 'auth/popup-blocked':
      return 'Pop-up was blocked. Allow pop-ups for this site and retry.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      if (err instanceof Error && err.message) return err.message;
      return 'Something went wrong. Please try again.';
  }
};
